import React, { useState, useCallback, useEffect, useRef } from 'react';
import { apiService } from '../services/api';
import ReactGA from 'react-ga4';
import Clarity from '@microsoft/clarity';

const VideoComparison = ({ user }) => {
  const [urls, setUrls] = useState(['', '']);
  const [videos, setVideos] = useState([null, null]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [progress, setProgress] = useState([0, 0]);
  const [errors, setErrors] = useState(['', '']);
  const [playlistMode, setPlaylistMode] = useState(false);
  const [playlistUrls, setPlaylistUrls] = useState(['', '']);
  const [playlistVideos, setPlaylistVideos] = useState([[], []]);
  const [isLoadingPlaylist, setIsLoadingPlaylist] = useState([false, false]);
  const [playlistStats, setPlaylistStats] = useState([null, null]);
  const [playlistComparison, setPlaylistComparison] = useState(null);
  const [isComparingPlaylists, setIsComparingPlaylists] = useState(false);
  const [analysisLanguage, setAnalysisLanguage] = useState("hinglish");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [playlistSearchQueries, setPlaylistSearchQueries] = useState(['', '']);
  const [playlistSearchResults, setPlaylistSearchResults] = useState([[], []]);
  const [isSearchingPlaylists, setIsSearchingPlaylists] = useState([false, false]);
  
  const dropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setIsCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const isValidYouTubeUrl = useCallback((url) => {
    if (!url || typeof url !== "string") return false;
    const patterns = [
      /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/(www\.)?youtube\.com\/watch\?.*v=([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/(www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
      /^https?:\/\/(www\.)?youtube\.com\/live\/([a-zA-Z0-9_-]{11})/,
    ];
    return patterns.some((pattern) => pattern.test(url.trim()));
  }, []);

  const isValidPlaylistUrl = useCallback((url) => {
    if (!url || typeof url !== "string") return false;
    return /youtube\.com\/.*[?&]list=([a-zA-Z0-9_-]+)/.test(url.trim());
  }, []);

  const comparePlaylists = async () => {
    if (!playlistUrls[0].trim() || !playlistUrls[1].trim()) {
      setErrors(['Please provide both playlist URLs', '']);
      return;
    }

    if (!playlistVideos[0].length || !playlistVideos[1].length) {
      setErrors(['Please load both playlists first', '']);
      return;
    }

    setIsComparingPlaylists(true);
    setErrors(['', '']);
    setPlaylistComparison(null);

    try {
      const response = await apiService.comparePlaylists(playlistUrls, analysisLanguage);
      
      if (response.success) {
        setPlaylistComparison(response);
        
        ReactGA.event({
          category: 'Comparison',
          action: 'Playlists Compared',
          label: 'Playlist Comparison'
        });
        
        Clarity.event('playlists_compared');
      }
    } catch (error) {
      setErrors([error.message || 'Failed to compare playlists', '']);
    } finally {
      setIsComparingPlaylists(false);
    }
  };

  const handleLoadAndComparePlaylists = async () => {
    const loadResults = await Promise.all([
      loadPlaylistVideos(0),
      loadPlaylistVideos(1)
    ]);

    if (loadResults.every(Boolean)) {
      await comparePlaylists();
    }
  };

  const searchPlaylistsOnYouTube = async (playlistIndex) => {
    const query = playlistSearchQueries[playlistIndex];
    if (!query || query.trim().length < 2) {
      const newErrors = [...errors];
      newErrors[playlistIndex] = 'Enter at least 2 characters to search playlists';
      setErrors(newErrors);
      return;
    }

    setIsSearchingPlaylists(prev => {
      const newState = [...prev];
      newState[playlistIndex] = true;
      return newState;
    });

    try {
      const response = await apiService.searchYouTube(query, 25);
      const playlists = response?.results?.playlists || [];
      setPlaylistSearchResults(prev => {
        const newResults = [...prev];
        newResults[playlistIndex] = playlists;
        return newResults;
      });
      const newErrors = [...errors];
      newErrors[playlistIndex] = playlists.length === 0 ? 'No playlists found for that query' : '';
      setErrors(newErrors);
    } catch (error) {
      const newErrors = [...errors];
      newErrors[playlistIndex] = error.message || 'Playlist search failed';
      setErrors(newErrors);
    } finally {
      setIsSearchingPlaylists(prev => {
        const newState = [...prev];
        newState[playlistIndex] = false;
        return newState;
      });
    }
  };

  const handleSelectPlaylistResult = (playlistIndex, playlist) => {
    const playlistUrl = `https://www.youtube.com/playlist?list=${playlist.id}`;
    const newUrls = [...playlistUrls];
    newUrls[playlistIndex] = playlistUrl;
    setPlaylistUrls(newUrls);
    const updatedErrors = [...errors];
    updatedErrors[playlistIndex] = '';
    setErrors(updatedErrors);
  };

  const loadPlaylistVideos = async (playlistIndex) => {
    let loadSucceeded = false;
    const playlistUrl = playlistUrls[playlistIndex];
    if (!isValidPlaylistUrl(playlistUrl)) {
      const newErrors = [...errors];
      newErrors[playlistIndex] = 'Invalid playlist URL';
      setErrors(newErrors);
      return false;
    }

    setIsLoadingPlaylist(prev => {
      const newState = [...prev];
      newState[playlistIndex] = true;
      return newState;
    });
    
    const newErrors = [...errors];
    newErrors[playlistIndex] = '';
    setErrors(newErrors);
    
    try {
      // Load playlist videos
      const response = await apiService.extractPlaylistVideos(playlistUrl);
      
      if (response.success && response.videos) {
        const videoUrls = response.videos.map(v => 
          `https://www.youtube.com/watch?v=${v.video_id}`
        );
        
        // Update playlist videos
        setPlaylistVideos(prev => {
          const newPlaylists = [...prev];
          newPlaylists[playlistIndex] = response.videos;
          return newPlaylists;
        });
        
        // Update URLs for this playlist (use first video for comparison)
        setUrls(prev => {
          const newUrls = [...prev];
          newUrls[playlistIndex] = videoUrls[0] || '';
          return newUrls;
        });
        
        // Fetch playlist statistics
        try {
          const statsResponse = await apiService.getPlaylistStats(playlistUrl);
          if (statsResponse.success && statsResponse.data) {
            setPlaylistStats(prev => {
              const newStats = [...prev];
              newStats[playlistIndex] = statsResponse.data;
              return newStats;
            });
          }
        } catch (statsError) {
          console.warn('Failed to fetch playlist stats:', statsError);
          // Don't show error, just continue without stats
        }
        
        ReactGA.event({
          category: 'Comparison',
          action: 'Playlist Loaded',
          label: `Playlist ${playlistIndex + 1}: ${response.videos.length} videos`
        });
        
        Clarity.event('playlist_loaded');
        loadSucceeded = true;
      }
    } catch (error) {
      const newErrors = [...errors];
      newErrors[playlistIndex] = error.message || 'Failed to load playlist';
      setErrors(newErrors);
    } finally {
      setIsLoadingPlaylist(prev => {
        const newState = [...prev];
        newState[playlistIndex] = false;
        return newState;
      });
    }
    return loadSucceeded;
  };

  const analyzeAllVideos = async () => {
    let filledUrls;
    
    if (playlistMode) {
      filledUrls = urls.filter(url => url.trim());
      if (filledUrls.length < 2) {
        setErrors(['Please load both playlists', '']);
        return;
      }
    } else {
      // Existing validation for individual videos
      filledUrls = urls.filter(url => url.trim());
      
      if (filledUrls.length < 2) {
        setErrors(['Please enter at least 2 YouTube URLs', '']);
        return;
      }

      const newErrors = urls.map(url => {
        if (!url.trim()) return '';
        return isValidYouTubeUrl(url) ? '' : 'Invalid YouTube URL';
      });

      if (newErrors.some(err => err)) {
        setErrors(newErrors);
        return;
      }
    }

    setIsAnalyzing(true);
    setErrors(['', '']);
    setProgress([0, 0]);
    // Initialize videos array with nulls (always 2 videos)
    setVideos([null, null]);

    // Track comparison start
    ReactGA.event({
      category: 'Comparison',
      action: 'Comparison Started',
      label: `${filledUrls.length} videos`
    });
    Clarity.event('comparison_started');

    // Analyze all videos in parallel
    const analysisPromises = filledUrls.map(async (url, index) => {
      try {
        // Update progress
        const progressInterval = setInterval(() => {
          setProgress(prev => {
            const newProgress = [...prev];
            if (newProgress[index] < 90) {
              newProgress[index] = Math.min(newProgress[index] + 10, 90);
            }
            return newProgress;
          });
        }, 1000);

        const response = await apiService.analyzeVideo(url, {
          analysisLanguage: analysisLanguage,
          category: selectedCategory
        });
        
        clearInterval(progressInterval);
        setProgress(prev => {
          const newProgress = [...prev];
          newProgress[index] = 100;
          return newProgress;
        });

        // apiService returns { success: true, data: {...} }
        // Extract the actual video data from nested structure
        const result = response.data || response;
        console.log(`✅ Video ${index + 1} analyzed:`, {
          response_structure: Object.keys(response),
          video_id: result.video_id,
          title: result.video_metadata?.title,
          score: result.analysis?.gradi_score?.score
        });
        return { index, result, error: null };
      } catch (error) {
        return { index, result: null, error: error.message || 'Analysis failed' };
      }
    });

    const results = await Promise.all(analysisPromises);

    // Update videos and errors based on results using functional updates
    setVideos(prevVideos => {
      const updated = [...prevVideos];
      results.forEach(({ index, result }) => {
        if (result) {
          updated[index] = result;
        }
      });
      return updated;
    });

    setErrors(prevErrors => {
      const updated = [...prevErrors];
      results.forEach(({ index, error }) => {
        if (error) {
          updated[index] = error;
        }
      });
      return updated;
    });

    setIsAnalyzing(false);

    // Track successful comparisons
    const successCount = results.filter(r => r.result).length;
    ReactGA.event({
      category: 'Comparison',
      action: 'Comparison Completed',
      label: `${successCount}/${filledUrls.length} successful`
    });
  };

  const clearAll = () => {
    setUrls(['', '']);
    setVideos([null, null]);
    setProgress([0, 0]);
    setErrors(['', '']);
    setPlaylistMode(false);
    setPlaylistUrls(['', '']);
    setPlaylistVideos([[], []]);
    setIsLoadingPlaylist([false, false]);
    setPlaylistStats([null, null]);
    setPlaylistComparison(null);
    setIsComparingPlaylists(false);
  };

  const getScoreColor = (score) => {
    if (score >= 4) return '#10b981';
    if (score >= 3) return '#f59e0b';
    return '#ef4444';
  };

  const getCategoryDifference = (category) => {
    const scores = videos
      .filter(v => v && v.analysis?.ratings?.[category])
      .map(v => v.analysis.ratings[category].score);
    
    if (scores.length < 2) return null;
    
    const max = Math.max(...scores);
    const min = Math.min(...scores);
    return (max - min).toFixed(1);
  };

  const getBestInCategory = (category) => {
    let bestIndex = -1;
    let bestScore = -1;

    videos.forEach((video, idx) => {
      if (video && video.analysis?.ratings?.[category]) {
        const score = video.analysis.ratings[category].score;
        if (score > bestScore) {
          bestScore = score;
          bestIndex = idx;
        }
      }
    });

    return bestIndex;
  };

  const categories = [
    'Clarity of Content',
    'Commercial Balance',
    'Content Depth',
    'Student Interaction',
    'Content Structure',
    'Communication Effectiveness'
  ];

  const activeVideos = videos.filter(v => v !== null);
  const canCompare = activeVideos.length >= 2;

  // Generate floating particles
  const generateParticles = () => {
    const particles = [];
    const colors = ['#8b5cf6', '#ec4899', '#06b6d4', '#10b981'];
    
    for (let i = 0; i < 50; i++) {
      particles.push(
        <div
          key={i}
          className="particle"
          style={{
            width: Math.random() * 4 + 2 + 'px',
            height: Math.random() * 4 + 2 + 'px',
            background: `linear-gradient(45deg, ${colors[Math.floor(Math.random() * 4)]}, transparent)`,
            left: Math.random() * 100 + '%',
            top: Math.random() * 100 + '%',
            animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
            animationDelay: Math.random() * 5 + 's'
          }}
        />
      );
    }
    return particles;
  };

  return (
    <div className="page-container">
      {/* Animated Background Elements */}
      <div className="animated-background" />

      {/* Floating particles */}
      <div className="floating-particles">
        {generateParticles()}
      </div>

      <div className="page-content">
        {/* Hero Section */}
        <div className="hero-section">
          {/* Main Heading */}
          <h1 className="hero-title" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', marginBottom: '0.5rem' }}>
            Video Comparison
          </h1>

          {/* Subheading */}
          <p className="hero-subtitle" style={{ fontSize: 'clamp(0.85rem, 1.5vw, 1rem)', marginBottom: '1.25rem' }}>
            Compare 2 videos side-by-side or compare 2 playlists
          </p>

          {/* Mode Toggle */}
          <div style={{
            display: 'inline-flex',
            justifyContent: 'center',
            gap: '0.375rem',
            marginBottom: '1.5rem',
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(20px)',
            borderRadius: '12px',
            padding: '0.375rem',
            border: '2px solid rgba(139, 92, 246, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }}>
            <button
              onClick={() => {
                setPlaylistMode(false);
                setPlaylistUrls(['', '']);
                setPlaylistVideos([[], []]);
              }}
              style={{
                padding: '0.625rem 1.5rem',
                borderRadius: '10px',
                border: 'none',
                background: !playlistMode
                  ? 'linear-gradient(135deg, #8b5cf6, #ec4899)'
                  : 'transparent',
                color: !playlistMode ? '#ffffff' : '#9ca3af',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: !playlistMode ? '700' : '500',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: !playlistMode ? '0 4px 12px rgba(139, 92, 246, 0.4)' : 'none'
              }}
            >
              Individual Videos
            </button>
            
            <button
              onClick={() => {
                setPlaylistMode(true);
                setUrls(['', '']);
                setPlaylistVideos([[], []]);
              }}
              style={{
                padding: '0.625rem 1.5rem',
                borderRadius: '10px',
                border: 'none',
                background: playlistMode
                  ? 'linear-gradient(135deg, #8b5cf6, #ec4899)'
                  : 'transparent',
                color: playlistMode ? '#ffffff' : '#9ca3af',
                cursor: 'pointer',
                fontSize: '0.85rem',
                fontWeight: playlistMode ? '700' : '500',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: playlistMode ? '0 4px 12px rgba(139, 92, 246, 0.4)' : 'none'
              }}
            >
              Playlist Comparison
            </button>
          </div>

          {/* Playlist Input */}
          {playlistMode && (
            <div className="results-container" style={{
              marginBottom: '2rem',
              overflowX: 'hidden',
              boxSizing: 'border-box'
            }}>
              <h3 style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                marginBottom: '1.5rem',
                color: '#e5e7eb',
                textAlign: 'center'
              }}>
                Enter Playlist URLs
              </h3>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '1.5rem',
                marginBottom: '1rem'
              }}>
                {[0, 1].map((index) => (
                  <div key={index}>
                    <h4 style={{
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      marginBottom: '0.75rem',
                      color: '#e5e7eb'
                    }}>
                      Playlist {index + 1}
                    </h4>
                    
                    <input
                      type="text"
                      value={playlistUrls[index]}
                      onChange={(e) => {
                        const newUrls = [...playlistUrls];
                        newUrls[index] = e.target.value;
                        setPlaylistUrls(newUrls);
                      }}
                      placeholder="https://www.youtube.com/playlist?list=..."
                      disabled={isLoadingPlaylist[index]}
                      className="input-field"
                      style={{
                        width: '100%',
                        marginBottom: '0.75rem',
                        boxSizing: 'border-box'
                      }}
                    />
                    
                    <div style={{
                      display: 'flex',
                      gap: '0.5rem',
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <input
                        type="text"
                        value={playlistSearchQueries[index]}
                        onChange={(e) => {
                          const newQueries = [...playlistSearchQueries];
                          newQueries[index] = e.target.value;
                          setPlaylistSearchQueries(newQueries);
                        }}
                        placeholder="Search playlists on YouTube..."
                        className="input-field"
                        style={{
                          padding: '0.6rem 1rem',
                          fontSize: '0.9rem',
                          flex: 1,
                          minWidth: 'auto',
                          marginBottom: 0
                        }}
                      />
                      <button
                        onClick={() => searchPlaylistsOnYouTube(index)}
                        disabled={isSearchingPlaylists[index]}
                        className="analyze-button"
                        style={{
                          minWidth: '140px',
                          padding: '0.6rem 1rem',
                          fontSize: '0.85rem'
                        }}
                      >
                        {isSearchingPlaylists[index] ? 'Searching...' : 'Search'}
                      </button>
                    </div>

                    {isLoadingPlaylist[index] && (
                      <div style={{
                        marginBottom: '0.5rem',
                        color: '#9ca3af',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.4rem'
                      }}>
                        <span className="spinner" />
                        Loading playlist...
                      </div>
                    )}

                    {errors[index] && (
                      <div className="error-container" style={{
                        marginTop: '0.75rem',
                        padding: '0.75rem'
                      }}>
                        <p className="error-content">{errors[index]}</p>
                      </div>
                    )}

                    {playlistSearchResults[index] && playlistSearchResults[index].length > 0 && (
                      <div style={{
                        marginTop: '0.75rem',
                        maxHeight: '220px',
                        overflowY: 'auto',
                        padding: '0.75rem',
                        borderRadius: '12px',
                        background: 'rgba(15, 15, 35, 0.75)',
                        border: '1px solid rgba(139, 92, 246, 0.25)'
                      }}>
                        <p style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.5rem', textAlign: 'left' }}>
                          Select a playlist to auto-fill:
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {playlistSearchResults[index].map((playlist) => (
                            <button
                              key={playlist.id}
                              onClick={() => handleSelectPlaylistResult(index, playlist)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.6rem',
                                padding: '0.45rem 0.6rem',
                                borderRadius: '10px',
                                border: '1px solid rgba(139, 92, 246, 0.35)',
                                background: 'rgba(0, 0, 0, 0.35)',
                                color: '#f8fafc',
                                textAlign: 'left',
                                cursor: 'pointer'
                              }}
                            >
                              {playlist.thumbnail && (
                                <img
                                  src={playlist.thumbnail}
                                  alt={playlist.title}
                                  style={{ width: '48px', height: '48px', borderRadius: '8px', objectFit: 'cover' }}
                                />
                              )}
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.15rem' }}>
                                  {playlist.title}
                                </div>
                                <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                  {playlist.itemCount ? `${playlist.itemCount} videos` : 'Playlist'}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {playlistVideos[index] && playlistVideos[index].length > 0 && (
                      <div style={{
                        marginTop: '0.75rem',
                        padding: '0.75rem',
                        background: 'rgba(16, 185, 129, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        color: '#10b981',
                        fontSize: '0.85rem',
                        textAlign: 'center'
                      }}>
                        ✅ {playlistVideos[index].length} videos loaded
                      </div>
                    )}

                    {/* Playlist Stats */}
                    {playlistStats[index] && (
                      <div className="step-card" style={{
                        marginTop: '1rem',
                        padding: '1rem',
                        background: 'rgba(139, 92, 246, 0.05)',
                        border: '2px solid rgba(139, 92, 246, 0.3)'
                      }}>
                        <h5 style={{
                          fontSize: '0.95rem',
                          fontWeight: 'bold',
                          marginBottom: '0.75rem',
                          color: '#e5e7eb'
                        }}>
                          Playlist Statistics
                        </h5>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(2, 1fr)',
                          gap: '0.75rem'
                        }}>
                          <div>
                            <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Total Views</div>
                            <div style={{ color: '#8b5cf6', fontSize: '1.1rem', fontWeight: '700' }}>
                              {playlistStats[index].total_views.toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Total Likes</div>
                            <div style={{ color: '#ec4899', fontSize: '1.1rem', fontWeight: '700' }}>
                              {playlistStats[index].total_likes.toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Avg Views</div>
                            <div style={{ color: '#06b6d4', fontSize: '1.1rem', fontWeight: '700' }}>
                              {playlistStats[index].avg_views.toLocaleString()}
                            </div>
                          </div>
                          <div>
                            <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>Engagement</div>
                            <div style={{ color: '#10b981', fontSize: '1.1rem', fontWeight: '700' }}>
                              {playlistStats[index].engagement_rate}%
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Load & Compare Button */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: '1.5rem'
              }}>
                <button
                  onClick={handleLoadAndComparePlaylists}
                  disabled={
                    isComparingPlaylists ||
                    isLoadingPlaylist.some(Boolean) ||
                    playlistUrls.some(url => !url.trim())
                  }
                  className="analyze-button"
                  style={{
                    minWidth: '220px',
                    fontSize: '1rem',
                    padding: '0.85rem 1.75rem'
                  }}
                >
                  {isComparingPlaylists || isLoadingPlaylist.some(Boolean) ? (
                    <>
                      <span className="spinner" />
                      Loading & Comparing...
                    </>
                  ) : (
                    'Load & Compare'
                  )}
                </button>
              </div>
            </div>
          )}

        </div>

        {/* Video Input Area */}
        {!playlistMode && (
        <div className="results-container" style={{
          marginBottom: '1.5rem',
          overflowX: 'hidden',
          boxSizing: 'border-box',
          padding: '1.25rem'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1.25rem',
            marginBottom: '1.5rem'
          }} className="video-input-grid">
            {Array.from({ length: 2 }).map((_, index) => (
              <div key={index}>
                <h3 style={{
                  fontSize: '0.95rem',
                  fontWeight: 'bold',
                  marginBottom: '0.75rem',
                  color: '#e5e7eb'
                }}>
                  Video {index + 1}
                </h3>

                <input
                  type="text"
                  value={urls[index] || ''}
                  onChange={(e) => {
                    const newUrls = [...urls];
                    newUrls[index] = e.target.value;
                    setUrls(newUrls);
                  }}
                  placeholder="Enter YouTube URL..."
                  disabled={isAnalyzing}
                  className="input-field"
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    marginBottom: '0.75rem',
                    padding: '0.7rem 1rem',
                    fontSize: '0.9rem',
                    opacity: isAnalyzing ? 0.6 : 1,
                    border: errors[index] ? '2px solid rgba(239, 68, 68, 0.6)' : undefined,
                    boxSizing: 'border-box'
                  }}
                />

                {errors[index] && (
                  <div className="error-container" style={{
                    marginBottom: '1rem',
                    padding: '1rem'
                  }}>
                    <p className="error-content">{errors[index]}</p>
                  </div>
                )}

                {isAnalyzing && urls[index] && (
                  <div style={{
                    marginTop: '1rem'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem',
                      fontSize: '0.9rem',
                      color: '#d1d5db'
                    }}>
                      <span>Analyzing...</span>
                      <span>{progress[index]}%</span>
                    </div>
                    <div className="progress-bar-container">
                      <div className="progress-bar" style={{
                        width: `${progress[index]}%`
                      }} />
                    </div>
                  </div>
                )}

                {videos[index] && (
                  <div className="results-container" style={{
                    marginTop: '1.5rem',
                    padding: '1.5rem'
                  }}>
                    <div style={{
                      fontSize: '1rem',
                      color: '#e5e7eb',
                      marginBottom: '1rem',
                      fontWeight: '600',
                      lineHeight: '1.4'
                    }}>
                      {videos[index].video_metadata?.title || videos[index].title || 'Video ' + (index + 1)}
                    </div>
                    <div style={{
                      fontSize: '2.5rem',
                      fontWeight: '800',
                      color: getScoreColor(videos[index].analysis?.gradi_score?.score || 0),
                      marginBottom: '1rem',
                      letterSpacing: '-0.02em',
                      background: `linear-gradient(135deg, ${getScoreColor(videos[index].analysis?.gradi_score?.score || 0)}, ${getScoreColor(videos[index].analysis?.gradi_score?.score || 0)}88)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}>
                      {(videos[index].analysis?.gradi_score?.score || 0).toFixed(1)}/5.0
                    </div>
                    {videos[index].analysis?.one_liner && (
                      <p className="score-message" style={{
                        fontSize: '0.95rem',
                        fontStyle: 'italic',
                        paddingTop: '1rem',
                        borderTop: '1px solid rgba(139, 92, 246, 0.2)',
                        marginTop: '1rem',
                        marginBottom: 0
                      }}>
                        {videos[index].analysis.one_liner}
                      </p>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Analysis Options - Centered between video columns */}
          <div style={{
            display: 'flex',
            gap: '0.75rem',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '1.5rem',
            marginBottom: '1.5rem'
          }}>
              <div ref={dropdownRef} className="custom-dropdown" onClick={() => !isAnalyzing && setIsDropdownOpen(!isDropdownOpen)} style={{ minWidth: '180px' }}>
                <div className={`language-select ${isDropdownOpen ? 'open' : ''} ${isAnalyzing ? 'disabled' : ''}`} style={{ padding: '0.7rem 2.5rem 0.7rem 1.25rem', fontSize: '0.9rem' }}>
                  <span className="dropdown-text">
                    {analysisLanguage === "english" ? "English Analysis" : "Hinglish Analysis"}
                  </span>
                  <span className={`dropdown-arrow ${isDropdownOpen ? 'rotated' : ''}`}>
                    ▾
                  </span>
                </div>
                {isDropdownOpen && !isAnalyzing && (
                  <div className="dropdown-options">
                    <div 
                      className={`dropdown-option ${analysisLanguage === 'hinglish' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnalysisLanguage('hinglish');
                        setIsDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">Hinglish Analysis</span>
                      {analysisLanguage === 'hinglish' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${analysisLanguage === 'english' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setAnalysisLanguage('english');
                        setIsDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">English Analysis</span>
                      {analysisLanguage === 'english' && <span className="check-icon">●</span>}
                    </div>
                  </div>
                )}
              </div>
              <div ref={categoryDropdownRef} className="custom-dropdown" onClick={() => !isAnalyzing && setIsCategoryDropdownOpen(!isCategoryDropdownOpen)} style={{ minWidth: '180px' }}>
                <div className={`category-select ${isCategoryDropdownOpen ? 'open' : ''} ${isAnalyzing ? 'disabled' : ''}`} style={{ padding: '0.7rem 2.5rem 0.7rem 1.25rem', fontSize: '0.9rem' }}>
                  <span className="dropdown-text">
                    {selectedCategory || "Auto-Detect Category"}
                  </span>
                  <span className={`dropdown-arrow ${isCategoryDropdownOpen ? 'rotated' : ''}`}>
                    ▾
                  </span>
                </div>
                {isCategoryDropdownOpen && !isAnalyzing && (
                  <div className="dropdown-options">
                    <div 
                      className={`dropdown-option ${selectedCategory === null ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory(null);
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">Auto-Detect Category</span>
                      {selectedCategory === null && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'Banking' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('Banking');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">Banking</span>
                      {selectedCategory === 'Banking' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'SSC' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('SSC');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">SSC</span>
                      {selectedCategory === 'SSC' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'Railway' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('Railway');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">Railway</span>
                      {selectedCategory === 'Railway' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'Teaching' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('Teaching');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">Teaching</span>
                      {selectedCategory === 'Teaching' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'Defence' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('Defence');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">Defence</span>
                      {selectedCategory === 'Defence' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'State Exams' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('State Exams');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">State Exams</span>
                      {selectedCategory === 'State Exams' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'General Studies' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('General Studies');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">General Studies</span>
                      {selectedCategory === 'General Studies' && <span className="check-icon">●</span>}
                    </div>
                    <div 
                      className={`dropdown-option ${selectedCategory === 'General' ? 'selected' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory('General');
                        setIsCategoryDropdownOpen(false);
                      }}
                    >
                      <span className="option-text">General</span>
                      {selectedCategory === 'General' && <span className="check-icon">●</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'center',
              alignItems: 'center',
              marginTop: '0.75rem'
            }}>
              <button
                onClick={analyzeAllVideos}
                disabled={isAnalyzing || urls.filter(u => u.trim()).length < 2}
                className="analyze-button"
                style={{
                  minWidth: '150px',
                  padding: '0.7rem 1.5rem',
                  fontSize: '0.9rem'
                }}
              >
                {isAnalyzing ? (
                  <>
                    <span className="spinner" />
                    Analyzing...
                  </>
                ) : (
                  'Compare Videos'
                )}
              </button>

              <button
                onClick={clearAll}
                disabled={isAnalyzing}
                className="analyze-button"
                style={{
                  background: 'rgba(239, 68, 68, 0.2)',
                  borderColor: 'rgba(239, 68, 68, 0.4)',
                  color: '#fca5a5',
                  minWidth: '150px',
                  padding: '0.7rem 1.5rem',
                  fontSize: '0.9rem'
                }}
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* Playlist Comparison Results */}
        {playlistMode && playlistComparison && (
          <div className="results-container" style={{
            marginBottom: '2rem'
          }}>
            <h2 style={{
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              fontWeight: 'bold',
              marginBottom: '2rem',
              color: '#e5e7eb',
              textAlign: 'center'
            }}>
              Playlist Comparison Results
            </h2>
            
            {/* Statistics Comparison */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '1.5rem',
              marginBottom: '2rem'
            }}>
              {playlistComparison.playlist_stats.map((stats, index) => (
                <div key={index} className="step-card" style={{
                  padding: '1.5rem'
                }}>
                  <h3 style={{
                    fontSize: 'clamp(1.1rem, 2.5vw, 1.4rem)',
                    fontWeight: 'bold',
                    marginBottom: '1rem',
                    color: '#e5e7eb',
                    textAlign: 'center'
                  }}>
                    Playlist {index + 1}
                  </h3>
                  
                  <div style={{
                    display: 'grid',
                    gap: '0.75rem'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.5rem 0',
                      borderBottom: '1px solid rgba(139, 92, 246, 0.2)'
                    }}>
                      <span style={{ color: '#9ca3af' }}>Videos:</span>
                      <span style={{ color: '#fff', fontWeight: '600' }}>{stats.video_count}</span>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.5rem 0',
                      borderBottom: '1px solid rgba(139, 92, 246, 0.2)'
                    }}>
                      <span style={{ color: '#9ca3af' }}>Total Views:</span>
                      <span style={{ color: '#8b5cf6', fontWeight: '600' }}>
                        {stats.total_views.toLocaleString()}
                      </span>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.5rem 0',
                      borderBottom: '1px solid rgba(139, 92, 246, 0.2)'
                    }}>
                      <span style={{ color: '#9ca3af' }}>Total Likes:</span>
                      <span style={{ color: '#ec4899', fontWeight: '600' }}>
                        {stats.total_likes.toLocaleString()}
                      </span>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.5rem 0',
                      borderBottom: '1px solid rgba(139, 92, 246, 0.2)'
                    }}>
                      <span style={{ color: '#9ca3af' }}>Total Comments:</span>
                      <span style={{ color: '#06b6d4', fontWeight: '600' }}>
                        {stats.total_comments.toLocaleString()}
                      </span>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.5rem 0',
                      borderBottom: '1px solid rgba(139, 92, 246, 0.2)'
                    }}>
                      <span style={{ color: '#9ca3af' }}>Avg Views/Video:</span>
                      <span style={{ color: '#fff', fontWeight: '600' }}>
                        {stats.avg_views.toLocaleString()}
                      </span>
                    </div>
                    
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '0.5rem 0',
                      borderBottom: '1px solid rgba(139, 92, 246, 0.2)'
                    }}>
                      <span style={{ color: '#9ca3af' }}>Engagement Rate:</span>
                      <span style={{ color: '#10b981', fontWeight: '600' }}>
                        {stats.engagement_rate}%
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Gemini Comparison Summary */}
            {playlistComparison.comparison_summary && (
              <div className="step-card" style={{
                padding: '2rem',
                background: 'rgba(139, 92, 246, 0.05)',
                border: '2px solid rgba(139, 92, 246, 0.3)'
              }}>
                <h3 style={{
                  fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)',
                  fontWeight: 'bold',
                  marginBottom: '1rem',
                  color: '#e5e7eb',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '1.5rem' }}>🤖</span>
                  Gradi's Analysis
                </h3>
                <div style={{
                  color: '#d4d4d4',
                  lineHeight: '1.8',
                  fontSize: 'clamp(0.95rem, 2vw, 1.1rem)',
                  whiteSpace: 'pre-wrap'
                }}>
                  {playlistComparison.comparison_summary}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Show loaded playlist videos */}
        {playlistMode && urls.filter(u => u.trim()).length > 0 && (
        <div className="results-container" style={{
          marginBottom: '2rem'
        }}>
          <h3 style={{
            fontSize: '1.2rem',
            fontWeight: 'bold',
            marginBottom: '1.5rem',
            color: '#e5e7eb',
            textAlign: 'center'
          }}>
            Videos from Playlists
          </h3>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '1.5rem'
          }}>
            {urls.filter(u => u.trim()).map((url, index) => {
              const playlistVideosList = playlistVideos[index] || [];
              return (
                <div key={index} className="step-card" style={{
                  padding: '1rem'
                }}>
                  <div style={{
                    fontSize: '0.95rem',
                    color: '#d1d5db',
                    marginBottom: '0.5rem',
                    fontWeight: 'bold'
                  }}>
                    Playlist {index + 1}
                  </div>
                  {playlistVideosList.length > 0 && (
                    <div style={{
                      fontSize: '0.85rem',
                      color: '#9ca3af',
                      marginBottom: '0.5rem'
                    }}>
                      {playlistVideosList.length} videos loaded
                    </div>
                  )}
                  {videos[index] && (
                    <div className="score-display" style={{
                      marginTop: '1rem',
                      padding: '1rem'
                    }}>
                      <div style={{
                        fontSize: '1.8rem',
                        fontWeight: 'bold',
                        color: getScoreColor(videos[index].analysis?.gradi_score?.score || 0),
                      }}>
                        {(videos[index].analysis?.gradi_score?.score || 0).toFixed(1)}/5.0
                      </div>
                    </div>
                  )}
                  {isAnalyzing && urls[index] && (
                    <div style={{ marginTop: '1rem' }}>
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '0.5rem',
                        fontSize: '0.9rem',
                        color: '#d1d5db'
                      }}>
                        <span>Analyzing...</span>
                        <span>{progress[index]}%</span>
                      </div>
                      <div className="progress-bar-container">
                        <div className="progress-bar" style={{
                          width: `${progress[index]}%`
                        }} />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        )}

        {/* Comparison Table */}
        {canCompare && (
          <div className="results-container" style={{
            marginBottom: '2rem'
          }}>
            <h2 className="hero-title" style={{
              fontSize: 'clamp(1.5rem, 3vw, 2rem)',
              marginBottom: '2rem'
            }}>
              Detailed Comparison
            </h2>

            <div style={{
              overflowX: 'auto'
            }}>
              <table style={{
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: '0 0.5rem'
              }}>
                <thead>
                  <tr>
                    <th style={{
                      textAlign: 'left',
                      padding: '1rem',
                      color: '#9ca3af',
                      fontSize: '1rem',
                      fontWeight: 'bold',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}>
                      Category
                    </th>
                    {videos.map((video, idx) => (
                      video && (
                        <th
                          key={idx}
                          style={{
                            textAlign: 'center',
                            padding: '1rem',
                            color: '#e5e7eb',
                            fontSize: '1rem',
                            fontWeight: 'bold'
                          }}
                        >
                          <div style={{
                            fontSize: '0.9rem',
                            fontWeight: 'bold',
                            marginBottom: '0.5rem',
                            color: '#e5e7eb',
                            textAlign: 'center',
                            padding: '0 0.5rem'
                          }}>
                            {video.video_metadata?.title || video.title || `Video ${idx + 1}`}
                          </div>
                          {video.analysis?.summary && (
                            <div style={{
                              fontSize: '0.75rem',
                              color: '#9ca3af',
                              lineHeight: '1.4',
                              marginTop: '0.5rem',
                              paddingTop: '0.5rem',
                              paddingLeft: '1rem',
                              paddingRight: '1rem',
                              borderTop: '1px solid rgba(139, 92, 246, 0.2)',
                              textAlign: 'center'
                            }}>
                              {video.analysis.summary}
                            </div>
                          )}
                        </th>
                      )
                    ))}
                    <th style={{
                      textAlign: 'center',
                      padding: '1rem',
                      color: '#9ca3af',
                      fontSize: '0.9rem',
                      fontWeight: 'bold'
                    }}>
                      Difference
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map((category) => {
                    const bestIndex = getBestInCategory(category);
                    const difference = getCategoryDifference(category);

                    return (
                      <tr key={category}>
                        <td style={{
                          padding: '1rem',
                          color: '#d1d5db',
                          fontSize: '1rem',
                          fontWeight: '500',
                          textTransform: 'capitalize',
                        background: 'rgba(15, 15, 35, 0.8)',
                        borderRadius: '8px 0 0 8px'
                        }}>
                          {category}
                        </td>
                        {videos.map((video, idx) => (
                          video && (
                            <td
                              key={idx}
                              style={{
                                textAlign: 'center',
                                padding: '1rem',
                                background: bestIndex === idx
                                  ? 'rgba(16, 185, 129, 0.15)'
                                  : 'rgba(15, 15, 35, 0.8)',
                                fontSize: '1.2rem',
                                fontWeight: 'bold',
                                color: bestIndex === idx ? '#10b981' : '#e5e7eb',
                                position: 'relative'
                              }}
                            >
                              {video.analysis?.ratings?.[category]?.score?.toFixed(1) || 'N/A'}
                              {bestIndex === idx && (
                                <span style={{
                                  marginLeft: '0.5rem',
                                  fontSize: '1rem'
                                }}>
                                  ✓
                                </span>
                              )}
                            </td>
                          )
                        ))}
                        <td style={{
                          textAlign: 'center',
                          padding: '1rem',
                          background: 'rgba(15, 15, 35, 0.8)',
                          borderRadius: '0 8px 8px 0',
                          color: '#9ca3af',
                          fontSize: '1rem'
                        }}>
                          {difference ? `±${difference}` : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Export Button */}
            <div style={{
              marginTop: '2rem',
              display: 'flex',
              justifyContent: 'center'
            }}>
              <button
                onClick={() => {
                  const data = {
                    timestamp: new Date().toISOString(),
                    videos: videos.filter(v => v).map(v => ({
                      title: v.video_metadata?.title || v.title,
                      video_id: v.video_id,
                      score: v.analysis?.gradi_score,
                      one_liner: v.analysis?.one_liner,
                      summary: v.analysis?.summary,
                      ratings: v.analysis?.ratings
                    }))
                  };
                  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `video-comparison-${new Date().getTime()}.json`;
                  a.click();

                  ReactGA.event({
                    category: 'Comparison',
                    action: 'Export Comparison',
                    label: 'JSON'
                  });
                }}
                className="analyze-button"
              >
                Export Comparison
              </button>
            </div>
          </div>
        )}

        {!canCompare && activeVideos.length > 0 && activeVideos.length < 2 && (
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            color: '#9ca3af',
            fontSize: '1.1rem'
          }}>
            Add at least 2 videos to compare
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoComparison;
